import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SelectMenu from '../../forms/SelectMenu';
import { TripLegStatusReason } from '../../../data/models/trip-leg-status-reason.model';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as lookups from '../../../data/lookup.slice';

const PREFIX = 'CancelTripLegDialog';

const classes = {
  content: `${PREFIX}-content`,
  message: `${PREFIX}-message`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.message}`]: {
    marginBottom: theme.spacing(2),
  },
}));

interface CancelReservationDialogProps {
  open: boolean;
  close: (data: any | null) => void;
  dialogTitle?: string;
  dialogMessage?: string;
  submitDataFsElement?: string;
}

export default function CancelTripLegDialog({
  open,
  close,
  dialogTitle,
  dialogMessage,
  submitDataFsElement,
}: CancelReservationDialogProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');

  const cancelReasons: TripLegStatusReason[] = useAppSelector(
    lookups.selectTripLegStatusReasons,
  );

  const [isValid, setIsValid] = React.useState(false);

  const formHook = useForm();

  const values = formHook.watch();
  React.useEffect(() => {
    let isVal = false;
    Object.keys(values).forEach((key) => (isVal ||= values[key]));
    setIsValid(isVal);
  }, [values]);

  const handleSave = (data: any) => {
    formHook.reset();
    close(data);
  };

  const handleClose = () => {
    formHook.reset();
    close(null);
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>{dialogTitle || t('passenger.cancelTripLeg')}</DialogTitle>
      <DialogContent className={classes.content}>
        <Typography className={classes.message}>
          {dialogMessage || t('passenger.cancelTripLegMessage')}
        </Typography>
        <SelectMenu
          label={t('passenger.reason')}
          name="CancelReason"
          values={cancelReasons}
          valueAttr="tripLegStatusReasonBC"
          labelAttr="tripLegStatusReasonName"
          required={true}
          control={formHook.control}
          rules={{ required: requiredFieldMessage }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.no')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!isValid}
          onClick={formHook.handleSubmit(handleSave)}
          data-fs-element={submitDataFsElement}>
          {t('common.yes')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
